import React, { useState, useRef, useEffect } from 'react';
import { X, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { Toast } from '../ui/Toast';

interface DeleteAccountModalProps {
  userEmail: string;
  onClose: () => void;
  onConfirmDelete: (password: string) => Promise<void>;
}

export function DeleteAccountModal({ userEmail, onClose, onConfirmDelete }: DeleteAccountModalProps) {
  const [password, setPassword] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deletionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (deletionTimeoutRef.current) {
        clearTimeout(deletionTimeoutRef.current);
      }
    };
  }, []);

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    if (!confirmChecked) {
      setError('Please confirm that you understand this action cannot be undone');
      return;
    }

    setLoading(true);

    try {
      setShowToast(true);
      setIsDeleting(true);

      deletionTimeoutRef.current = setTimeout(async () => {
        try {
          await onConfirmDelete(password);
        } catch (err: any) {
          console.error('Error during account deletion:', err);
          setError(err.message || 'Failed to delete account');
          setShowToast(false);
          setIsDeleting(false);
          setLoading(false);
        }
      }, 10000);
    } catch (err: any) {
      console.error('Error starting deletion:', err);
      setError(err.message || 'Failed to initiate account deletion');
      setLoading(false);
    }
  };

  const handleUndo = () => {
    if (deletionTimeoutRef.current) {
      clearTimeout(deletionTimeoutRef.current);
      deletionTimeoutRef.current = undefined;
    }
    setShowToast(false);
    setIsDeleting(false);
    setLoading(false);
    setPassword('');
    setConfirmChecked(false);
    setError(null);
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[250] p-4">
        <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg backdrop-blur-xl w-full max-w-md flex flex-col text-white">
          <div className="p-6 border-b border-white/10">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <Trash2 className="w-5 h-5 text-red-400" />
                </div>
                <h2 className="text-xl text-white font-brand font-light italic">Delete Account</h2>
              </div>
              <button
                onClick={onClose}
                disabled={isDeleting}
                className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="space-y-2 text-sm">
                <p className="text-red-200 font-medium">Warning: This action cannot be undone</p>
                <p className="text-white/70">
                  Deleting your account will permanently remove all of your data, including:
                </p>
                <ul className="list-disc list-inside text-white/60 space-y-1 ml-2">
                  <li>All sourdough starters</li>
                  <li>All baking sessions</li>
                  <li>All bread analyses</li>
                  <li>Challenge progress and results</li>
                  <li>Notification preferences</li>
                  <li>All uploaded images</li>
                </ul>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 backdrop-blur-sm rounded-lg border border-red-500/20 text-red-200 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleDelete} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Confirm your password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isDeleting}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-red-400/50 focus:ring-2 focus:ring-red-400/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter your password"
                  required
                />
                <p className="mt-2 text-xs text-white/50">
                  Signed in as: <span className="text-white/70">{userEmail}</span>
                </p>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="confirm-delete"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  disabled={isDeleting}
                  className="mt-1 w-4 h-4 bg-white/10 border-white/20 rounded focus:ring-2 focus:ring-red-400/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <label htmlFor="confirm-delete" className="text-sm text-white/70">
                  I understand that this action is permanent and cannot be undone. All my data will be deleted immediately.
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !password.trim() || !confirmChecked}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                    loading || !password.trim() || !confirmChecked
                      ? 'bg-white/10 text-white/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-red-500/90 to-red-600/90 hover:from-red-500 hover:to-red-600 text-white'
                  }`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Account
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showToast && (
        <Toast
          message="Account deletion scheduled. You have 10 seconds to undo."
          onClose={handleCloseToast}
          onUndo={handleUndo}
          duration={10000}
        />
      )}
    </>
  );
}
